<template>
    <base-gantt
        ref="basegantt"
        :tasks="tasks"
        :range="range"
    ></base-gantt>
</template>

<script>

import BaseGantt from './BaseGantt';
import toCurrency from '../../filters/toCurrency';
import moment from 'moment';
import Lang from '../../Lang';

export default {

    components: {
        BaseGantt,
    },

    computed: {
        getTotalDays() {
            return parseInt(moment(this.range[0]).diff(moment(this.range[1]), 'days') * -1) + 1; // +1 to add the current day as well.
        },
    },

    data() {
        return {
            defaultRecord: {
                type:"open",
                uuid:null,
            }
        }
    },

    methods: {

        attachEventHandlers() {
            // On task click handler
            gantt.attachEvent("onTaskClick", (id, link) => {
                this.$emit("task-clicked", id, link);
                return true;
            });

            gantt.attachEvent("onAfterTaskDelete", (id, item) => {
                // Todo: This event also fires whenever we're removing tasks manually...
                //console.log("Deleted task: " + item.id);
            });
        },

        getDateRecord(user_id, date, part) {
            if (!this.plannedstate[user_id]) {
                return JSON.parse(JSON.stringify(this.defaultRecord));
            }

            let ps = this.plannedstate[user_id];

            let key = date + '-' + part;

            if (typeof ps === "undefined" || !ps[key]) {
                return JSON.parse(JSON.stringify(this.defaultRecord));
            }

            return JSON.parse(JSON.stringify(ps[key]));
        },

        getTaskButtons(){
            return '<a href="javascript:void(0);" style="color:#000;"><i class="fa fa-plus"></i></a>';
        },

        getTaskEndDate(task_id) {
            let task = this.tasks.data.filter(task => task.is_task === true && task.id === task_id);

            return task.length !== 1 ?
                moment().add(1, 'year').unix() :
                moment(task[0].end_date).unix();
        },

        getUserButtons(){
            return '<a href="javascript:void(0);" style="color:#000;"><i class="fa fa-times"></i></a>';
        },

        setDefaultConfig(){
            gantt.config.date_grid = "%Y-%m-%d ";
            gantt.config.xml_date = "%Y-%m-%d %H:%i:%s";
            gantt.config.duration_unit = "day";
            gantt.config.work_time = true;
            gantt.config.scale_unit = "month";
            gantt.config.step = 1;
            gantt.config.date_scale = "%F, %Y";
            gantt.config.min_column_width = 20;
            gantt.config.scale_height = 90;
            gantt.config.row_height = 22;
            gantt.config.grid_width = 300;
            //gantt.config.static_background = true;

            gantt.config.readonly = true;
            gantt.config.drag_progress = false;
        },

        setColumns() {
            // Setup colums array
            let columns = [];
            // Add the text column
            columns.push({name:"text",label:Lang.get("tasks.task"),tree:true,width:'*',resize:true});
            // Check if the budget should be available
            if (this.showBudget) {
                // Add the budget columns to the array
                columns.push({
                    name:"budget",label:Lang.get("tasks.budget"),align:"right",width:90,resize:true,template(obj){
                        if ((!obj.budget && obj.budget !== 0) || obj.budget === '') { return ''; }
                        if (obj.budget < 0) {
                            return '<span style="color:red;">' + toCurrency(obj.budget) + '</span>';
                        }
                        return toCurrency(obj.budget);
                    }
                });
            }
            // Check if the buttons should be made available
            if (this.showButtons || this.specialProjectUuid !== '') {
                // Add them to the colums array
                columns.push({
                    name:"buttons",
                    label:'',
                    width:30,
                    template:(obj) => {
                        if (!this.showButtons && obj.project_uuid !== this.specialProjectUuid) {
                            return "";
                        }
                        // Check if project
                        if (obj.is_project) { return ""; }
                        else if (obj.is_task) { return this.getTaskButtons(); }
                        else { return this.getUserButtons(); }
                    }
                });
            }
            // Set the columns
            gantt.config.columns = columns;
        },

        setSubscale() {
            gantt.config.subscales = [
                {
                    unit:"week",step:1,template:(date) => {
                        return "Week " + gantt.date.getISOWeek(date);
                    }
                },
                {
                    unit:"day",step:1,template:(date) => {
                        let dateToStr = gantt.date.date_to_str("%d");
                        return dateToStr(date);
                    }
                }
            ];
        },

        setTemplates() {
            /*
             |
             | The timeline_cell_class template determintes if a cell (a single day on a row) needs an additional class.
             | We're using this for days where a user is on a holiday or where the day is in the weekend.
             |
             */
            gantt.templates.timeline_cell_class = (task, date) => {
                // Check if weekend
                if (date.getDay() === 0 || date.getDay() === 6) {
                    // Return the weekend class
                    return "weekend";
                }
                // Check highlight
                if (task.highlight) {
                    return 'highlight-row';
                }
            };
            /*
             |
             | The task_class template adds an additional class to a single row whenever we're handling a user row.
             |
             */
            gantt.templates.task_class = (start, end, task) => {
                if (task.is_user) {
                    return "complex_gantt_bar";
                }
            };
            /*
             |
             | The task_class template adds an additional class to a single row whenever we're handling a user row.
             |
             */
            gantt.templates.grid_row_class = (start, end, task) => {
                if (task.highlight) {
                    return 'highlight-row';
                }
            };
            /*
             |
             | The task_text template in the component is basically killing the default behaviour of gantt to
             | replace it with functionality required by the planning overview page. The default task bar will not
             | be rendered because of this template. Instead some magic will be happening to write a bunch of cells
             | where the availability of a user is shown immediately.
             |
             */
            gantt.templates.task_text = (start, end, task) => {
                // If the current task is not a user, skip it and simply return the text value
                if (!task.is_user) {
                    return task.text;
                }

                let taskstartdate = moment(task.start_date).unix();
                let taskenddate = this.getTaskEndDate(task.parent);

                let cell_width = 100 / this.getTotalDays;

                let string = '';

                let user_id = task.id.split("---")[1];

                // Loop all days for the row
                for (let x = 0; x < this.getTotalDays; x++) {
                    let currentdate = moment(this.range[0]).add(12, 'hour').add(x, "day");

                    if (currentdate.day() === 0 ||
                        currentdate.day() === 6 ||
                        currentdate.unix() < taskstartdate ||
                        currentdate.unix() > taskenddate
                    ) {
                        continue;
                    }

                    for (let part = 1; part <= (this.halfDays === true ? 2 : 1); part++) {
                        let formatted = currentdate.format("YYYY-MM-DD");
                        let record = this.getDateRecord(user_id, formatted, part);

                        let bgcolor, title;

                        switch (record.type) {
                            default:
                            case "open":{
                                bgcolor = "#fff";
                                title = Lang.get("planning.states.notplanned");
                                break;
                            }
                            case "planned":{
                                if (record.uuid === task.parent) {
                                    bgcolor = "#007fff";
                                    title = Lang.get("planning.states.thistask");
                                }
                                else {
                                    bgcolor = "#d3d3d3";

                                    if (gantt.isTaskExists(record.uuid)) {
                                        title = gantt.getTask(record.uuid).text;
                                    }
                                }
                                break;
                            }
                            case "holiday":{
                                bgcolor = "#ff7f7f";
                                title = Lang.get("planning.states.holiday");
                                break;
                            }
                            case "not-available":{
                                bgcolor = "#ff7f7f";
                                title = Lang.get("planning.states.not-available");
                                break;
                            }
                            case 'partial-work-day':
                            case 'partial-holiday':
                                bgcolor = 'linear-gradient(135deg, rgba(255,255,255,1) 50%, rgba(209,205,202,1) 50%)';
                                title = record.title;
                                break;
                        }
                        // Set default styling
                        let stylesobj = {
                            'border-color':'#bdbdbd',
                            'background': bgcolor,
                            'top': '12%',
                            'left':(x * cell_width) + '%',
                            'width': cell_width + '%',
                            'height':'80%',
                        };
                        if (this.halfDays === true) {
                            stylesobj.width = (cell_width / 2) + '%';
                        }
                        // Check if the buttons need to be shown and if the currentdate variable is later or the same as today (actual date)
                        if ((this.showButtons || task.project_uuid === this.specialProjectUuid) && currentdate.diff(moment(), "days") >= 0 && record.type !== "holiday" && record.type !== "not-available") {
                            if (this.halfDays === true && part === 2) {
                                stylesobj.left = ((x * cell_width) + (cell_width / 2)) + '%';
                            }
                            string += '<a class="gantt_task_line archibase_planning_item" id="' + task.id + "---" + x + "---" + part + '" style="' + this.toStyle(stylesobj) + '" title="' + title + '" data-date="' + formatted + '"></a>';
                        } else {
                            stylesobj.cursor = 'context-menu';
                            if (this.halfDays === true && part === 2) {
                                stylesobj.left = ((x * cell_width) + (cell_width / 2)) + '%';
                            }
                            string += '<span class="gantt_task_line" id="' + task.id + "---" + x + "---" + part + '" style="' + this.toStyle(stylesobj) + '" title="' + title + '" data-date="' + formatted + '"></span>';
                        }
                    }
                }
                // Return the html string
                return string;
            };
        },

        onPlannedItemClick(id, withshift) {
            let exploded = id.split("---");
            this.$emit(
                "planned-item-clicked",
                exploded[0] + "---" + exploded[1] + "---" + exploded[3],
                moment(this.range[0] + " 12:00:00").add(exploded[2], "day"),
                withshift
            );
        },

        toStyle(stylesobject) {
            let string = JSON.stringify(stylesobject);
            return string.replaceAll('":"', ':')
                .replaceAll('","',';')
                .replaceAll('{"', '')
                .replaceAll('"}', '');
        },

    },

    mounted() {
        this.setDefaultConfig();
        this.setColumns();
        this.setSubscale();
        this.setTemplates();
        this.attachEventHandlers();
        this.$refs.basegantt.startGantt();

        this.$nextTick(function () {
            let $this = this;
            jQuery(document).on("click", "a.archibase_planning_item", function(e){
                $this.onPlannedItemClick(jQuery(this).attr("id"), e.shiftKey);
            });
        }.bind(this));
    },

    name: "GanttPlanning",

    props: {
        halfDays: {
            type:Boolean,
            default:false,
        },
        tasks: {
            required:true,
            type:Object,
            default:{
                data: [],
                links: [],
            }
        },
        range: {
            required:true,
            type:Array,
            default:['',''],
        },
        plannedstate: {
            required:true,
            default:{},
        },
        showBudget: {
            type:Boolean,
            default:false,
        },
        showButtons: {
            type:Boolean,
            default:false,
        },
        specialProjectUuid: {
            type: String,
            default: '',
        },
    },
}

</script>
